import * as React from "react";
import { icons as LUCIDE, type LucideProps } from "lucide-react";
import { cn } from "./utils";
import { NUCLEO_REGISTRY } from "./icons/nucleo";

/**
 * The single icon rendering surface for the app (design-language refresh).
 *
 * Why this exists: 98 files imported `lucide-react` directly, so the icon
 * stroke (the new language wants 1px) and the glyph backend (Nucleo) could
 * not be changed in one place. `<Icon>` centralizes both — it defaults to a
 * 16px, **1px-stroke**, `currentColor` glyph so it inherits whatever text
 * tier it sits in, and resolves glyphs Nucleo-first, then lucide (the 1px
 * bridge). See docs/design-language/PRD.md §5.3.
 *
 * Two call styles:
 *   <Icon name="search" />        // preferred for new code (Nucleo-swappable)
 *   <Icon as={SomeLucideIcon} />  // the migration bridge for existing sites
 *
 * Note: a global `svg.lucide { stroke-width: 1px }` rule in globals.css
 * guarantees 1px on every lucide glyph regardless of how it is rendered, so
 * the visual contract holds even for icons not yet routed through `<Icon>`.
 */

/** Type-scale-aligned icon sizes (px). */
const SIZE_PX = { xs: 12, sm: 14, base: 16, lg: 20 } as const;
export type IconSize = keyof typeof SIZE_PX | number;

export interface IconProps extends Omit<LucideProps, "ref" | "size"> {
  /** kebab-case glyph name; resolved Nucleo registry → lucide. */
  name?: string;
  /** Render a passed icon component directly (the lucide migration bridge). */
  as?: React.ComponentType<LucideProps>;
  /** Scale key (`xs|sm|base|lg`) or explicit px. Default `base` (16px). */
  size?: IconSize;
}

function pascalize(kebab: string): string {
  return kebab
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

function resolve(
  name?: string,
  as?: React.ComponentType<LucideProps>,
): React.ComponentType<LucideProps> | null {
  if (as) return as;
  if (!name) return null;
  const nucleo = NUCLEO_REGISTRY[name];
  if (nucleo) return nucleo as React.ComponentType<LucideProps>;
  const registry = LUCIDE as unknown as Record<
    string,
    React.ComponentType<LucideProps>
  >;
  return registry[pascalize(name)] ?? registry.HelpCircle ?? null;
}

export function Icon({
  name,
  as,
  size = "base",
  strokeWidth = 1,
  className,
  ...rest
}: IconProps) {
  const Cmp = resolve(name, as);
  if (!Cmp) return null;
  const px = typeof size === "number" ? size : SIZE_PX[size];
  return (
    <Cmp
      width={px}
      height={px}
      strokeWidth={strokeWidth}
      aria-hidden={rest["aria-label"] ? undefined : true}
      className={cn("shrink-0", className)}
      {...rest}
    />
  );
}
